// askSmart-ui/src/pages/MapleCoin/money.ts
import { Currency, MinorString, TransactionDirection } from './types';

export const CURRENCIES: Record<Currency, { name: string; places: number }> = {
  CAD: { name: 'Canadian Dollar', places: 2 },
  USD: { name: 'US Dollar', places: 2 },
};

const MINOR = /^-?(0|[1-9][0-9]*)$/;

/**
 * Exact decimal text from a minor-unit string, by string manipulation only —
 * no floating point ever touches the amount. "10000000" → "100,000.00".
 * Returns the absolute value; the sign is handled by the callers.
 */
export function formatDecimal(minor: MinorString, currency: Currency): string {
  if (!MINOR.test(minor)) throw new Error(`Not a minor-unit amount: ${minor}`);
  const places = CURRENCIES[currency].places;
  const digits = minor.replace(/^-/, '').padStart(places + 1, '0');
  const whole = digits.slice(0, digits.length - places).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const fraction = digits.slice(digits.length - places);
  return places > 0 ? `${whole}.${fraction}` : whole;
}

/** "CAD $100,000.00" — the currency code is always shown, so "$" is never ambiguous. */
export function formatMoney(minor: MinorString, currency: Currency): string {
  const negative = minor.startsWith('-') && /[1-9]/.test(minor);
  return `${negative ? '−' : ''}${currency} $${formatDecimal(minor, currency)}`;
}

/**
 * A transaction amount, signed by its business direction — money coming in
 * is "+", going out is "−". Never derived from ledger debit/credit terms.
 */
export function formatSignedAmount(minor: MinorString, currency: Currency, direction: TransactionDirection): string {
  return `${direction === 'CREDIT' ? '+' : '−'} ${currency} $${formatDecimal(minor, currency)}`;
}

/** True when an exact minor-unit amount is zero (e.g. no pending funds). */
export function isZero(minor: MinorString): boolean {
  return /^-?0+$/.test(minor);
}
